//import moment from "moment";
import moment from "utils/moment/momentLocale";
import { i18n } from "engine/Contexts/AppProviders";
import {
  timeFormat,
  dateFormat,
  dateAndTimeFormat,
  dateFormatCompare,
  timeFormatCompare,
} from "utils/moment/moment.constant";
import { DAY_OF_WEEK_OPTIONS } from 'utils/helpers/helper.constant';

const initLang = localStorage.getItem('i18nextLng') || process.env.DEFAULT_LANGUAGE || "fr";

export const convertSecondsToTime = function (time, format = `HH:MM`) {
  return !!time && moment(time).format(format);
};
export const convertTimeToString = function (time, format, separator = ":") {
  return !!time && moment(time).format(format || `HH${separator}mm`);
};
export const convertDateToString = function (date, format) {
  return !!date && moment(date).format(format || dateFormat);
};
export const convertDateTimeToString = function (date, format) {
  return !!date && moment(date).format(format || dateAndTimeFormat);
};
export const convertDateToISOString = function(date) {
  return !!date && moment(date).toISOString();
};
export const convertDateToWeekDay = function (date, format = "dddd") {
  return !!date && moment(date).format(format);
};

/* Seconds To... */
export function secondsToTime(secs) {
  let days = Math.floor(secs / (60 * 60) / 24);
  let hours = Math.floor(secs / (60 * 60));

  let divisor_for_minutes = secs % (60 * 60);
  let minutes = Math.floor(divisor_for_minutes / 60);

  let divisor_for_seconds = divisor_for_minutes % 60;
  let seconds = Math.ceil(divisor_for_seconds);

  let obj = {
    d: days,
    h: hours,
    m: minutes,
    s: seconds,
  };
  return obj;
}
export function secondsToTimeLabel(secs) {
  if (!!secs && Number(secs) > 0) {
    const timeObj = secondsToTime(
      secs,
    );

    let hour =
      timeObj?.h !==
      0
        ? timeObj?.h +
          "h"
        : "";
    let min =
      timeObj?.m !==
      0
        ? timeObj?.m +
          ""
        : "";

    return hour
      ? `${hour}${
          min
            ? `${min}`
            : ""
        }`
      : min
      ? `${min} minutes`
      : "";
  } else {
    return "";
  }
}
export function secondsToTimeLabelAbs(secs) {
  const timeObj = secondsToTime(secs);

  let hour = timeObj?.h !== 0 ? timeObj?.h + " heures" : "";
  let min = timeObj?.m !== 0 ? timeObj?.m + "" : "";

  return hour
    ? `${hour}${min ? ` ${min} minutes` : ""}`
    : min
    ? `${min} minutes`
    : "";
}

/* Between Dates... */
export const isTimeBetween = function(aStartTime, anEndTime, aCurrTime) {
  // you may pass in aCurrTime or use the *actual* current time
  let currentTime = !aCurrTime ? moment() : moment(aCurrTime, "HH:mm a");
  let startTime = moment(aStartTime, "HH:mm a");
  let endTime = moment(anEndTime, "HH:mm a");

  if (startTime.hour() >= 12 && endTime.hour() <= 12) {
    endTime.add(1, "days"); // handle spanning days
  }

  let isBetween = currentTime.isBetween(startTime, endTime);

  return isBetween;
};
export const isDatesBetween = (aStartDate, anEndDate, aCurrDate) => {
  return (
    aStartDate &&
    anEndDate &&
    aCurrDate &&
    moment(aCurrDate, dateFormatCompare).isBetween(
      moment(aStartDate, dateFormatCompare),
      moment(anEndDate, dateFormatCompare),
    )
  );
};
export const getDaysBetweenDates = function(startDate, endDate) {
  let now = moment(startDate).clone(),
    dates = [];

  const momentEndDate = moment(endDate);

  while (now.isSameOrBefore(momentEndDate)) {
    dates.push(now.format(dateFormatCompare));
    now.add(1, "days");
  }
  return dates;
};

export const isToday = function(SpecialToDate) {
  if (SpecialToDate) {
    const checkingDate = moment(SpecialToDate);
    return {
      today: moment().format(dateFormat),
      isSame: moment()
        .startOf("day")
        .isSame(checkingDate.startOf("day"), "day"),
    };
  } else {
    return null;
  }
};

/* Same Dates... */
export const isSameDates = function(dateOne, dateTwo) {
  if (!(dateOne && dateTwo)) return false;

  return moment(dateOne)
    .startOf("day")
    .isSame(moment(dateTwo).startOf("day"), "day");
};

export const isSameTimes = (timeOne, timeTwo) => {
  return (
    timeOne &&
    timeTwo &&
    moment(timeOne, timeFormatCompare).isSame(
      moment(timeTwo, timeFormatCompare),
    )
  );
};

export function calculateBusinessDays(d1, d2) {
  const days = d2.diff(d1, "days") + 1;
  let newDay = d1.toDate(),
    workingDays = 0,
    sundays = 0,
    saturdays = 0;

  for (let i = 0; i < days; i++) {
    const day = newDay.getDay();
    newDay = d1.add(1, "days").toDate();
    const isWeekend = day % 6 === 0;
    if (!isWeekend) {
      workingDays++;
    } else {
      if (day === 6) saturdays++;
      if (day === 0) sundays++;
    }
  }

  return workingDays;
}

export const isDateBeforeToday = SpecialToDate => {
  if (!!SpecialToDate) {
    const now = moment().clone();
    const checkingDate = moment(SpecialToDate);
    const diffValue = now
      .startOf("day")
      .diff(checkingDate.startOf("day"), "days");
    return diffValue > 0;
  } else {
    return null;
  }
};

export const isDateAfterToday = SpecialToDate => {
  if (SpecialToDate) {
    const now = moment().clone();
    const checkingDate = moment(SpecialToDate);

    const diffValue = now
      .startOf("day")
      .diff(checkingDate.startOf("day"), "days");

    return diffValue < 0;
  } else {
    return null;
  }
};

export const isDateBeforeDate = (dateOne, dateTwo) => {
  if (dateOne && dateTwo) {
    const now = moment(dateOne).clone();
    const checkingDate = moment(dateTwo);
    const diffValue = now
      .startOf("day")
      .diff(checkingDate.startOf("day"), "days");
    return diffValue > 0;
  } else {
    return null;
  }
};

export const isDateBeforeDateBySeconds = (dateOne, dateTwo) => {
  if (dateOne && dateTwo) {
    const now = moment(dateOne).clone();
    const checkingDate = moment(dateTwo);
    const diffValue = now.diff(checkingDate, "seconds");
    return diffValue > 0;
  } else {
    return null;
  }
};

export const isDateAfterDate = (dateOne, dateTwo) => {
  if (dateOne && dateTwo) {
    const now = moment(dateOne).clone();
    const checkingDate = moment(dateTwo);

    const diffValue = now
      .startOf("day")
      .diff(checkingDate.startOf("day"), "days");
    return diffValue < 0;
  } else {
    return null;
  }
};

export const compareTimeRangeAndSetDefault = (timeOne, timeTwo) => {
  if (timeOne && timeTwo) {
    const timeOneMoment = moment(timeOne, timeFormatCompare);
    const timeTwoMoment = moment(timeTwo, timeFormatCompare);

    const isSame = timeTwoMoment.isSame(timeOneMoment, "seconds");
    const isAfter = timeTwoMoment.isAfter(timeOneMoment, "seconds");
    const isBefore = timeTwoMoment.isBefore(timeOneMoment, "seconds");
    const diffTime = timeTwoMoment.diff(timeOneMoment, "seconds");
    return {
      isSame: isSame,
      isAfter: isAfter,
      isBefore: isBefore,
      diff: diffTime > 0 ? diffTime / 60 : 0,
    };
  } else {
    return null;
  }
};

export const compareDateRangeAndSetDefault = (dateOne, dateTwo) => {
  if (dateOne && dateTwo) {
    const dateOneMoment = moment(dateOne, dateFormatCompare);
    const dateTwoMoment = moment(dateTwo, dateFormatCompare);

    const isSame = isSameDates(dateOne, dateTwo);
    const isAfter = isDateAfterDate(dateOne, dateTwo);
    const isBefore = isDateBeforeDate(dateOne, dateTwo);
    const diffDays = dateTwoMoment
      .startOf("day")
      .diff(dateOneMoment.startOf("day"), "days");
    return {
      isSame: isSame,
      isAfter: isAfter,
      isBefore: isBefore,
      diff: diffDays > 0 ? diffDays : 0,
    };
  } else {
    return null;
  }
};

export const getDayOfWeek = date => {
  /* const today = new Date(); // Current date and time
today.setHours(0, 0, 0, 0); // Reset time to midnight

const dayOfWeek = today.getDay(); // Get day of the week (0-6) */
  const now = moment().clone();
  const currentDate = date ? moment(date) : now;
  return DAY_OF_WEEK_OPTIONS()[currentDate.isoWeekday() - 1]?.value;
};
export const getEndDateBasedOnPeriod = (dateFrom, period = "week") => {
  const startDate = moment(dateFrom); // Replace with your start date
  const periodType = period; // Replace with 'month' for monthly periods
  const periodCount = 1; // Replace with the number of periods

  // Calculate the end date based on the period type and count
  const endDate = startDate.clone().add(periodCount, periodType);

  // Adjust the end date to match the same day of the week as the start date
  while ( endDate.day() !== startDate.day()) {
    endDate.subtract(1,"day");
  }

  return {
    weekDay: DAY_OF_WEEK_OPTIONS()[startDate.isoWeekday() - 1],
    diff: endDate.startOf("day").diff(startDate.startOf("day"), "days"),
    startDateFormat: startDate.format("DD.MM.YYYY"),
    startDate,
    endDateFormat: endDate.format("DD.MM.YYYY"),
    endDate: endDate.startOf("day").add(moment.duration(12, 'hours'))
  };
};

export const generateDateRange = (from, to, modifier = "week", step = 1) => {
  if (from && to) {
    const dates = [];
    let currentDate = moment(from);
    const lastDate = moment(to);
    const dayWeek = getDayOfWeek(from);
    const dayNumber = currentDate.isoWeekday();

    while (currentDate <= lastDate) {
      dates.push(currentDate.format(dateFormat));
      currentDate = currentDate.add(step, modifier);
    }

    const lastDayInRange = moment(dates?.[dates?.length - 1], dateFormat);
    const lastDayWeekDate = lastDate.day(dayNumber);
    const isAfter = isDateAfterDate(to, lastDayWeekDate);
    const isBefore = isDateBeforeDate(to, lastDayWeekDate);

    let latestDay = lastDayInRange;
    if (isAfter) {
      latestDay = lastDayInRange;
    } else if (isBefore) {
      latestDay = lastDayWeekDate;
    }

    const sameDates = isSameDates(to, latestDay);
    if (!!sameDates && getDayOfWeek(latestDay) != dayWeek) {
      const closestPreviousDayOfWeek = latestDay
        .clone()
        .subtract(1, "week");
      latestDay = closestPreviousDayOfWeek.day(dayNumber);
    }
    return {
      today: moment().format(dateFormat),
      dates: dates,
      actualDates: dates.slice(1),
      lastDayOfPeriod: getEndDateBasedOnPeriod(from, modifier),
      dayWeek: dayWeek,
      lastDayInRange: lastDayInRange.toISOString(),
      lastDayofFullPeriod: lastDayWeekDate.toISOString(),
      latestDay: latestDay.toISOString(),
    };
  } else {
    return null;
  }
};

export const getBusinessDays = function(day1, day2) {
  if (!!day1 && !!day2) {
    const d1 = moment(day1);
    const d2 = moment(day2);
    const sameDate = isSameDates(d1, d2);
    const days = d2.startOf("day").diff(d1.startOf("day"), "days") + 1;
    let newDay = d1.startOf("day").toDate(),
      workingDays = 0,
      sundays = 0,
      saturdays = 0,
      workingDates = [];
    const dates = [];

    for (let i = 0; i < days; i++) {
      const day = newDay.getDay();
      dates.push({
        day: moment(newDay).format(dateFormat),
        date: moment(newDay, dateFormat).toISOString(),
        weekDay: getDayOfWeek(newDay),
      });
      const isWeekend = day % 6 === 0;
      if (!isWeekend) {
        workingDays++;
        workingDates.push(moment(newDay).format(dateFormat));
      } else {
        if (day === 6) saturdays++;
        if (day === 0) sundays++;
      }
      newDay = moment(newDay)
        .add(1, "days")
        .toDate();
    }
    return {
      daysBetween: !!sameDate || !!!d1 || !!!d2 ? 0 : days,
      workingDays: !!sameDate || !!!d1 || !!!d2 ? 0 : workingDays,
      dates: !!sameDate || !!!d1 || !!!d2 ? [] : dates,
    };
  } else {
    return {
      daysBetween: 0,
      workingDays: 0,
      dates: [],
    };
  }
};

export const convertTimeForChat = timestamp => {
  const now = moment();
  const messageTime = moment(timestamp);
  const diff = now.diff(messageTime, "minutes");
  const isToday = now.isSame(messageTime, "day");

  if (diff < 50) {
    return messageTime.locale("fr").fromNow();
  } else if (isToday) {
    return messageTime.format("HH:mm");
  } else {
    return messageTime.format("D MMMM YYYY — HH:mm");
  }
};
